import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../database/firebase';
import toast from 'react-hot-toast';

const COLLECTIONS = {
  PROGRAMS: 'programs',
  USER_PROGRESS: 'userProgress',
  SUBMISSIONS: 'submissions',
  MODULES: 'modules',
  LESSONS: 'lessons',
};

export const PROGRAM_TYPES = {
  DESIGN_A_BUSINESS: 'design-a-business',
  COSTING_ACCOUNTING: 'costing-accounting',
  CAPACITY_BUILDING: 'capacity-building',
  SCALING_MODULES: 'scaling-modules',
};

export const SUBMISSION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  NEEDS_REVISION: 'needs-revision',
};

export const PROGRESS_STATUS = {
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
};

export class ProgramsService {
  static async getAllPrograms() {
    try {
      const programsRef = collection(firestore, COLLECTIONS.PROGRAMS);
      const q = query(programsRef);
      const snapshot = await getDocs(q);

      const programs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      return programs.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });
    } catch (error) {
      toast.error('Error fetching programs:', error);
      throw error;
    }
  }

  static async getProgramById(programId) {
    try {
      const programRef = doc(firestore, COLLECTIONS.PROGRAMS, programId);
      const programSnap = await getDoc(programRef);

      if (programSnap.exists()) {
        return {
          id: programSnap.id,
          ...programSnap.data(),
        };
      }
      return null;
    } catch (error) {
      toast.error('Error fetching program:', error);
      throw error;
    }
  }

  static async createProgram(programData) {
    try {
      const programsRef = collection(firestore, COLLECTIONS.PROGRAMS);
      const docRef = await addDoc(programsRef, {
        ...programData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...programData,
      };
    } catch (error) {
      toast.error('Error creating program:', error);
      throw error;
    }
  }

  static async updateProgram(programId, updateData) {
    try {
      const programRef = doc(firestore, COLLECTIONS.PROGRAMS, programId);
      await updateDoc(programRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      return await this.getProgramById(programId);
    } catch (error) {
      toast.error('Error updating program:', error);
      throw error;
    }
  }

  static async deleteProgram(programId) {
    try {
      const programRef = doc(firestore, COLLECTIONS.PROGRAMS, programId);
      await deleteDoc(programRef);
      toast.success('Program deleted successfully');
      await this.deleteProgramData(programId);
    } catch (error) {
      toast.error('Error deleting program:', error);
      throw error;
    }
  }

  static async deleteProgramData(programId) {
    try {
      const batch = writeBatch(firestore);

      const progressQuery = query(
        collection(firestore, COLLECTIONS.USER_PROGRESS),
        where('programId', '==', programId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      progressSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      const submissionsQuery = query(
        collection(firestore, COLLECTIONS.SUBMISSIONS),
        where('programId', '==', programId)
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);
      submissionsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    } catch (error) {
      toast.error('Error deleting program data:', error);
      throw error;
    }
  }
}

export class ProgressService {
  static async getUserProgress(userId) {
    try {
      const progressRef = collection(firestore, COLLECTIONS.USER_PROGRESS);
      const q = query(progressRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      toast.error('Error fetching user progress:', error);
      throw error;
    }
  }

  static async getUserProgress(userId, programId = null) {
    try {
      const progressRef = collection(firestore, COLLECTIONS.USER_PROGRESS);
      let q = query(progressRef, where('userId', '==', userId));

      if (programId) {
        q = query(progressRef, where('userId', '==', userId), where('programId', '==', programId));
      }

      const snapshot = await getDocs(q);

      if (programId) {
        if (snapshot.empty) {
          return null;
        }
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      } else {
        const progressArray = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        return progressArray.sort((a, b) => {
          if (a.updatedAt && b.updatedAt) {
            return b.updatedAt.toDate() - a.updatedAt.toDate();
          }
          return 0;
        });
      }
    } catch (error) {
      toast.error('Error fetching user progress:', error);
      throw error;
    }
  }

  static async updateProgress(userId, programId, progressData) {
    try {
      const existingProgress = await this.getProgramProgress(userId, programId);

      if (existingProgress) {
        const progressRef = doc(firestore, COLLECTIONS.USER_PROGRESS, existingProgress.id);
        await updateDoc(progressRef, {
          ...progressData,
          updatedAt: serverTimestamp(),
        });

        return {
          id: existingProgress.id,
          ...existingProgress,
          ...progressData,
        };
      } else {
        const progressRef = collection(firestore, COLLECTIONS.USER_PROGRESS);
        const docRef = await addDoc(progressRef, {
          userId,
          programId,
          ...progressData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        return {
          id: docRef.id,
          userId,
          programId,
          ...progressData,
        };
      }
    } catch (error) {
      toast.error('Error updating progress:', error);
      throw error;
    }
  }

  static async completeLesson(userId, programId, lessonId) {
    try {
      const progress = await this.getProgramProgress(userId, programId);
      const updatedLessons = progress?.lessons || {};

      updatedLessons[lessonId] = {
        completed: true,
        completedAt: serverTimestamp(),
      };

      const totalLessons = Object.keys(updatedLessons).length;
      const completedLessons = Object.values(updatedLessons).filter(
        lesson => lesson.completed
      ).length;
      const progressPercentage = Math.round((completedLessons / totalLessons) * 100);

      const status =
        progressPercentage === 100 ? PROGRESS_STATUS.COMPLETED : PROGRESS_STATUS.IN_PROGRESS;

      return await this.updateProgress(userId, programId, {
        lessons: updatedLessons,
        progressPercentage,
        status,
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      toast.error('Error completing lesson:', error);
      throw error;
    }
  }

  static async startLesson(userId, programId, lessonId) {
    try {
      const progress = await this.getProgramProgress(userId, programId);
      const updatedLessons = progress?.lessons || {};

      if (!updatedLessons[lessonId]) {
        updatedLessons[lessonId] = {
          started: true,
          startedAt: serverTimestamp(),
          completed: false,
        };
      } else {
        updatedLessons[lessonId].started = true;
        updatedLessons[lessonId].startedAt = serverTimestamp();
      }

      return await this.updateProgress(userId, programId, {
        lessons: updatedLessons,
        status: PROGRESS_STATUS.IN_PROGRESS,
        lastActivityAt: serverTimestamp(),
      });
    } catch (error) {
      toast.error('Error starting lesson:', error);
      throw error;
    }
  }

  static subscribeToProgress(userId, programId, callback) {
    const progressRef = collection(firestore, COLLECTIONS.USER_PROGRESS);
    const q = query(
      progressRef,
      where('userId', '==', userId),
      where('programId', '==', programId)
    );

    return onSnapshot(q, snapshot => {
      if (snapshot.empty) {
        callback(null);
      } else {
        const doc = snapshot.docs[0];
        callback({
          id: doc.id,
          ...doc.data(),
        });
      }
    });
  }

  static listenToUserProgress(userId, programId, callback) {
    return this.subscribeToProgress(userId, programId, callback);
  }
}

export class SubmissionsService {
  static async getUserSubmissions(userId, programId = null) {
    try {
      const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
      let q = query(submissionsRef, where('submittedBy', '==', userId));

      if (programId) {
        q = query(
          submissionsRef,
          where('submittedBy', '==', userId),
          where('programId', '==', programId)
        );
      }

      const snapshot = await getDocs(q);
      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      return submissions.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });
    } catch (error) {
      toast.error('Error fetching user submissions:', error);
      throw error;
    }
  }

  static async getAllSubmissions(status = null) {
    try {
      const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
      let q = query(submissionsRef);

      if (status) {
        q = query(submissionsRef, where('status', '==', status));
      }

      const snapshot = await getDocs(q);

      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      return submissions.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });
    } catch (error) {
      toast.error('Error fetching all submissions:', error);
      throw error;
    }
  }

  static async getSubmissionById(submissionId) {
    try {
      const submissionRef = doc(firestore, COLLECTIONS.SUBMISSIONS, submissionId);
      const submissionSnap = await getDoc(submissionRef);

      if (submissionSnap.exists()) {
        return {
          id: submissionSnap.id,
          ...submissionSnap.data(),
        };
      }
      return null;
    } catch (error) {
      toast.error('Error fetching submission:', error);
      throw error;
    }
  }

  static async createSubmission(userId, programId, submissionData, file = null) {
    try {
      let fileUrl = null;
      let fileMetadata = null;

      if (file) {
        const fileName = `${userId}_${programId}_${Date.now()}_${file.name}`;
        const fileRef = ref(storage, `submissions/${fileName}`);

        const uploadResult = await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(uploadResult.ref);

        fileMetadata = {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedAt: serverTimestamp(),
        };
      }

      const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
      const docRef = await addDoc(submissionsRef, {
        ...submissionData,
        submittedBy: userId,
        programId,
        fileUrl,
        fileMetadata,
        status: SUBMISSION_STATUS.PENDING,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...submissionData,
        submittedBy: userId,
        programId,
        fileUrl,
        fileMetadata,
        status: SUBMISSION_STATUS.PENDING,
      };
    } catch (error) {
      toast.error('Error creating submission:', error);
      throw error;
    }
  }

  static async updateSubmission(submissionId, updateData) {
    try {
      const submissionRef = doc(firestore, COLLECTIONS.SUBMISSIONS, submissionId);
      await updateDoc(submissionRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      return await this.getSubmissionById(submissionId);
    } catch (error) {
      toast.error('Error updating submission:', error);
      throw error;
    }
  }

  static async deleteSubmission(submissionId) {
    try {
      const submission = await this.getSubmissionById(submissionId);

      if (submission?.fileUrl) {
        const fileRef = ref(storage, submission.fileUrl);
        await deleteObject(fileRef);
      }

      const submissionRef = doc(firestore, COLLECTIONS.SUBMISSIONS, submissionId);
      await deleteDoc(submissionRef);
    } catch (error) {
      toast.error('Error deleting submission:', error);
      throw error;
    }
  }

  static subscribeToSubmissions(userId, programId = null, callback) {
    const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
    let q = query(submissionsRef, where('submittedBy', '==', userId));

    if (programId) {
      q = query(
        submissionsRef,
        where('submittedBy', '==', userId),
        where('programId', '==', programId)
      );
    }

    return onSnapshot(q, snapshot => {
      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const sortedSubmissions = submissions.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });

      callback(sortedSubmissions);
    });
  }

  static async getSubmissionsByProgram(programId) {
    try {
      const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
      const q = query(submissionsRef, where('programId', '==', programId));
      const snapshot = await getDocs(q);

      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      return submissions.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });
    } catch (error) {
      toast.error('Error fetching program submissions:', error);
      throw error;
    }
  }

  static listenToSubmissions(programId, callback) {
    const submissionsRef = collection(firestore, COLLECTIONS.SUBMISSIONS);
    const q = query(submissionsRef, where('programId', '==', programId));

    return onSnapshot(q, snapshot => {
      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const sortedSubmissions = submissions.sort((a, b) => {
        if (a.submittedAt && b.submittedAt) {
          return b.submittedAt.toDate() - a.submittedAt.toDate();
        }
        return 0;
      });

      callback(sortedSubmissions);
    });
  }
}

export class ModulesService {
  static async getProgramModules(programId) {
    try {
      const modulesRef = collection(firestore, COLLECTIONS.MODULES);
      const q = query(modulesRef, where('programId', '==', programId));
      const snapshot = await getDocs(q);

      const modules = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      return modules.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });
    } catch (error) {
      toast.error('Error fetching program modules:', error);
      throw error;
    }
  }

  static async getModuleLessons(moduleId) {
    try {
      const lessonsRef = collection(firestore, COLLECTIONS.LESSONS);
      const q = query(lessonsRef, where('moduleId', '==', moduleId), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      toast.error('Error fetching module lessons:', error);
      throw error;
    }
  }
}

export default {
  ProgramsService,
  ProgressService,
  SubmissionsService,
  ModulesService,
  PROGRAM_TYPES,
  SUBMISSION_STATUS,
  PROGRESS_STATUS,
};
